import React from 'react'
import {css} from '@emotion/react'
import styled from '@emotion/styled'
import {Size} from 'types/Theme'
import {ColorPath} from 'types/Theme'

import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {cssTheme} from '../../../../themes'
import {linariaMq} from '../../../../utils/breakpoints'
import {Badge} from '../../Badge'
import {Icon} from '../../Icon'
import {Tag} from '../../Tag'

export type InputBaseSize = Extract<Size, 'sm' | 'md' | 'lg'>

export enum TextTransform {
  uppercase = 'uppercase',
  capitalize = 'capitalize',
  lowercase = 'lowercase',
  none = 'none'
}

export enum InputTextType {
  text = 'text',
  password = 'password',
  email = 'email',
  tel = 'tel',
  search = 'search',
  textarea = 'textarea'
}

export const CLEAR_BUTTON_MIN_WIDTH = 14

interface StyleProps {
  showInvalid?: boolean
  showValid?: boolean
  isResizable?: boolean
  isRounded?: boolean
  sizeVariant?: InputBaseSize
  textTransformType?: TextTransform
  withBorder?: boolean
  withStartIcon?: boolean
  withEndIcon?: boolean
}

interface ValidationBadgeProps {
  isVerified?: boolean
  showInvalid?: boolean
  showValid?: boolean
}

interface IconsWrapperProps {
  isRounded?: boolean
  sizeVariant?: InputBaseSize
}

interface WithInsideLabelIconsWrapperProps {
  sizeVariant: InputBaseSize
  withRightShifting?: boolean
}

interface CaratWrapperProps {
  isOpen: boolean
  disabled?: boolean
  sizeVariant: InputBaseSize
}

interface IconSizeMapItem {
  padding: number
  rightIconWidth: number
  height: number
}

export const SM_INPUT_HEIGHT = 32
export const MD_INPUT_HEIGHT = 40
export const LG_INPUT_HEIGHT = 48

export const MD_INPUT_WITH_INSIDE_LABEL_HEIGHT = 48
export const LG_INPUT_WITH_INSIDE_LABEL_HEIGHT = 56

export const iconsSizeMap: Record<InputBaseSize, IconSizeMapItem> = {
  sm: {
    padding: 8,
    rightIconWidth: 30,
    height: SM_INPUT_HEIGHT
  },
  md: {
    padding: 10,
    rightIconWidth: 40,
    height: MD_INPUT_HEIGHT
  },
  lg: {
    padding: 12,
    rightIconWidth: 44,
    height: LG_INPUT_HEIGHT
  }
}

const iconsWithInsideLabelSizeMap: Record<InputBaseSize, IconSizeMapItem> = {
  sm: {
    padding: 12,
    rightIconWidth: 40,
    height: MD_INPUT_WITH_INSIDE_LABEL_HEIGHT
  },
  md: {
    padding: 12,
    rightIconWidth: 40,
    height: MD_INPUT_WITH_INSIDE_LABEL_HEIGHT
  },
  lg: {
    padding: 12,
    rightIconWidth: 44,
    height: LG_INPUT_WITH_INSIDE_LABEL_HEIGHT
  }
}

export const FS_PRIVACY_MASK_CLASS = 'fs-mask'
export const ICON_SIZE = 20
const VALIDATION_ICON_SIZE = 16

export const getBaseInputIconPaddingStyles = ({
  sizeVariant = 'lg',
  isRounded = false,
  withStartIcon = false,
  withEndIcon = true,
  showInvalid = false,
  showValid = false
}: StyleProps) => {
  const gap = cssTheme.layout.spacing.s250
  const roundedPadding = isRounded ? cssTheme.layout.spacing.s200 : '0px'
  const size = iconsSizeMap[sizeVariant]
  const withValidationIcon = showInvalid || showValid

  const paddingLeft = `calc(${size.padding}px + ${withStartIcon ? `${ICON_SIZE}px + ${gap}` : '0px'})`
  const paddingRight = `calc(${roundedPadding} + ${withEndIcon ? `${size.rightIconWidth}px` : '0px'} + ${withValidationIcon ? `${VALIDATION_ICON_SIZE}px + ${gap}` : '0px'})`

  return {
    paddingLeft,
    paddingRight
  }
}

export const getBaseInsideLabelIconPaddingStyles = ({
  sizeVariant = 'lg',
  withStartIcon = false,
  withEndIcon = true,
  showInvalid = false,
  showValid = false
}: StyleProps) => {
  const gap = cssTheme.layout.spacing.s250
  const size = iconsWithInsideLabelSizeMap[sizeVariant]
  const withValidationIcon = showInvalid || showValid

  const paddingLeft = `calc(${size.padding}px + ${withStartIcon ? `${ICON_SIZE}px + ${gap}` : '0px'})`
  const paddingRight = `calc(${withEndIcon ? `${size.rightIconWidth}px` : '0px'} + ${withValidationIcon ? `${VALIDATION_ICON_SIZE}px + ${gap}` : '0px'})`

  return {
    paddingLeft,
    paddingRight
  }
}

const BaseInsideLabelIconsWrapper = styled.div<WithInsideLabelIconsWrapperProps>`
  pointer-events: none;
  position: absolute;
  z-index: 3;
  display: flex;
  align-items: center;
  height: ${({sizeVariant = 'lg'}) =>
    iconsWithInsideLabelSizeMap[sizeVariant].height}px;
`

export const WithInsideLabelStartIconsWrapper = styled(
  BaseInsideLabelIconsWrapper
)<WithInsideLabelIconsWrapperProps>`
  top: 0;
  left: 0;
  padding-left: ${({sizeVariant}) =>
    iconsWithInsideLabelSizeMap[sizeVariant].padding}px;
`

export const WithInsideLabelEndIconsWrapper = styled(
  BaseInsideLabelIconsWrapper
)<WithInsideLabelIconsWrapperProps>`
  top: 0;
  right: ${({withRightShifting}) =>
    withRightShifting ? cssTheme.layout.spacing.s500 : 0};
  gap: ${cssTheme.layout.spacing.s200};
`

const BaseIconsWrapper = styled.div<IconsWrapperProps>`
  pointer-events: none;
  position: absolute;
  z-index: 3;
  display: flex;
  align-items: center;
  height: ${({sizeVariant = 'lg'}) => iconsSizeMap[sizeVariant].height}px;
`

export const StartIconsWrapper = styled(BaseIconsWrapper)<
  IconsWrapperProps & {sizeVariant: InputBaseSize}
>`
  top: 0;
  left: 1px;
  padding-left: ${({sizeVariant = 'lg'}) =>
    iconsSizeMap[sizeVariant].padding}px;
  color: ${cssTheme.colors.input.default.icon};
`

export const EndIconsWrapper = styled(BaseIconsWrapper)<IconsWrapperProps>`
  top: 0;
  right: 0;
  padding-right: ${({isRounded}) =>
    isRounded ? cssTheme.layout.spacing.s200 : 0};
  color: ${cssTheme.colors.input.default.icon};
`

export const focusShadowStyles = css`
  box-shadow: 0px 0px 0px 1px ${cssTheme.colors.input.focus.border};
`

export const baseInputShadowStyles = css`
  &:focus {
    ${focusShadowStyles};
  }
`

export const baseInputRadiusStyles = css`
  border-radius: ${cssTheme.layout.radius.lg};
  &.--size-sm {
    border-radius: ${cssTheme.layout.radius.sm};
  }
  &.--is-rounded {
    border-radius: ${cssTheme.layout.radius.rounded};
  }
`

export const baseInputBaseLayoutStyles = css`
  display: inline-flex;
  align-items: center;
  position: relative;
  outline: none;
  box-sizing: border-box;
  width: 100%;
  gap: ${cssTheme.layout.spacing.s250};
  border: 1px solid ${cssTheme.colors.input.default.border};
  color: ${cssTheme.colors.input.default.text};
  background: ${cssTheme.colors.input.default.background};
  -webkit-appearance: none;

  ::-webkit-search-cancel-button {
    display: none;
  }

  &::placeholder {
    color: ${cssTheme.colors.input.default.label.empty};
    text-transform: none;
  }
`

export const defaultBorderStyles = css`
  border-color: ${cssTheme.colors.input.default.border};
`

const activeBorderStyles = css`
  border-color: ${cssTheme.colors.input.focus.border};
`

export const focusBorderStyles = css`
  border-color: ${cssTheme.colors.input.focus.border};
`

const hoverBorderStyles = css`
  border-color: ${cssTheme.colors.input.hover.border};
`

const invalidBorderStyles = css`
  border-color: ${cssTheme.colors.input.error.border};
  z-index: 1;
`

export const baseInputBorderStyles = css`
  ${defaultBorderStyles}

  ${linariaMq.desktopXs} {
    &:hover {
      ${hoverBorderStyles}
    }
  }

  &:active {
    ${activeBorderStyles}
  }

  &:focus {
    ${focusBorderStyles}
  }
`

export const baseInputBackgroundStyles = css`
  ${linariaMq.desktopXs} {
    &:hover {
      background: ${cssTheme.colors.input.default.background};
    }
  }

  &:active {
    background: ${cssTheme.colors.input.default.background};
  }
`

export const baseInputTypographyStyles = css`
  &.--size-md {
    ${cssTheme.typography.bodyM};
  }
  &.--size-sm {
    ${cssTheme.typography.bodyXS};
  }
  &.--size-md {
    ${cssTheme.typography.bodyM};

    ${linariaMq.desktopXs} {
      ${cssTheme.typography.bodyS};
    }
  }
  &.--size-lg {
    ${cssTheme.typography.bodyM};

    ${linariaMq.desktopXs} {
      ${cssTheme.typography.bodyS};
    }
  }
`

export const baseInputSizeStyles = css`
  &.--size-sm {
    min-height: ${SM_INPUT_HEIGHT}px;
  }
  &.--size-md {
    min-height: ${MD_INPUT_HEIGHT}px;
  }
  &.--size-lg {
    min-height: ${LG_INPUT_HEIGHT}px;
  }
`

export const baseInputInvalidStateStyles = css`
  &.--is-invalid {
    ${invalidBorderStyles}
    &:active {
      ${invalidBorderStyles}
    }
    &:focus {
      ${focusBorderStyles};
    }
    ${linariaMq.desktopXs} {
      &:hover {
        ${invalidBorderStyles}
      }
    }
  }
`

export const CaptionWrapper = styled.div<{showInvalid?: boolean}>`
  padding: ${cssTheme.layout.spacing.s200} 0;
  color: ${({showInvalid}) =>
    showInvalid
      ? cssTheme.colors.input.error.caption
      : cssTheme.colors.input.default.caption};
`

const VerifiedTag = () => (
  <Tag
    variant="success"
    size="sm"
    iconStart={<Icon name="Checkmark" size="xs" />}
  >
    <FormattedMessageWrapper
      id="inputText.verified"
      defaultMessage="Verified"
    />
  </Tag>
)

export const ValidationBadge = ({
  isVerified = false,
  showInvalid,
  showValid
}: ValidationBadgeProps) => {
  if (!showInvalid && !showValid) return null

  const iconsName = showValid ? 'Checkmark' : 'WarningMark'
  const variant = showValid ? 'success' : 'danger'

  return isVerified ? (
    <VerifiedTag />
  ) : (
    <Badge iconName={iconsName} size="xs" variant={variant} />
  )
}

export const getClassName = (isPrivate?: boolean) =>
  isPrivate ? FS_PRIVACY_MASK_CLASS : undefined

export const ClearButtonWrapper = styled.div`
  pointer-events: auto;
  min-width: ${CLEAR_BUTTON_MIN_WIDTH}px;
`

export const CaratWrapper = styled.div<CaratWrapperProps>`
  transition: transform 0.2s ease 0s;
  display: flex;
  justify-content: center;
  transform: ${({isOpen}) => (isOpen ? 'rotateZ(0)' : 'rotateZ(180deg)')};
  color: ${({disabled}) =>
    disabled
      ? cssTheme.colors.input.disabled.icon
      : cssTheme.colors.input.default.icon};
  width: ${({sizeVariant}) => `${iconsSizeMap[sizeVariant].rightIconWidth}px`};
`

export const baseInputAppearanceNoneStyles = css`
  appearance: none;
`

interface GetInsideLabelTextColorPathParams {
  isFocused?: boolean
  isDisabled?: boolean
  hasError?: boolean
  hasValue?: boolean
  defaultOverride?: ColorPath
}

/**
 * Get the color path for the inside label text based on the input state
 * @param isFocused - is the input focused
 * @param isDisabled - is the input disabled
 * @param hasError - does the input have an error
 * @param hasValue - does the input have a value
 * @param defaultOverride - override the default color path
 * @returns the color path for the inside label text
 */
export const getInsideLabelTextColorPath = ({
  isFocused,
  isDisabled,
  hasError,
  hasValue,
  defaultOverride
}: GetInsideLabelTextColorPathParams): ColorPath => {
  if (isFocused) {
    return 'input.default.text'
  }
  if (isDisabled) {
    return 'input.disabled.text'
  }
  if (hasError) {
    return 'input.error.caption'
  }
  if (hasValue) {
    return 'input.default.caption'
  }

  return defaultOverride || 'input.default.text'
}
